import { JSX } from 'preact';

import { EditorProps, ViewerProps, RichProp } from 'client/courses/components/page-builder';
import { ContainerWrapper } from 'client/courses/components/page-builder/container-wrapper';
import {
  getIconClasses,
  getIconStyle,
} from 'client/courses/components/page-builder/card-style-utils';

import { CardLayoutBlock } from './card-layout-block';
import {
  extractReadonlyProps,
  extractBlockState,
  createGenerateState,
  SectionHeader,
} from './common-patterns';

import { CardStyles, IconStyles } from 'server/types/public-overrides';
import { Color } from 'shared/colors';
import { CredentialBlockState, PaddingSize } from 'server/types';

const DEFAULT_CREDENTIALS_CARD_STYLES: CardStyles = {
  borderRadius: '2xl',
  shadow: 'xl',
  borderWidth: 'medium',
  borderColor: '#e5e7eb',
  backgroundType: 'gradient',
  gradientFrom: '#ffffff',
  gradientTo: '#f9fafb',
  hoverLift: 'lg',
};

const DEFAULT_CREDENTIALS_ICON_STYLES: IconStyles = {
  shape: 'rounded',
  size: 'md',
  shadow: 'none',
  borderWidth: 'none',
  borderColor: '#e5e7eb',
  backgroundColor: '#f3f4f6',
};

export const type = 'calendar-credentials';
export const name = 'Credentials';

export const initialState: CredentialBlockState = {
  headerRichText: '<h2>Experience & Achievements</h2><p>What I Bring to Our Partnership</p>',
  cards: [
    {
      id: crypto.randomUUID(),
      iconRichText: '<p>🎓</p>',
      contentRichText:
        '<h3>Certified Life Design Coach</h3><p>Stanford Design School certification in evidence-based life design methodology</p>',
    },
    {
      id: crypto.randomUUID(),
      iconRichText: '<p>📚</p>',
      contentRichText:
        '<h3>Executive MBA</h3><p>Wharton Business School, specializing in leadership and organizational psychology</p>',
    },
    {
      id: crypto.randomUUID(),
      iconRichText: '<p>💼</p>',
      contentRichText:
        '<h3>Former SVP at Amazon</h3><p>Led 500+ person teams and scaled operations across multiple business units</p>',
    },
    {
      id: crypto.randomUUID(),
      iconRichText: '<p>✍️</p>',
      contentRichText:
        '<h3>Published Author</h3><p>"Design Your Next Chapter" (Penguin, 2023) - bestselling guide to career transitions</p>',
    },
    {
      id: crypto.randomUUID(),
      iconRichText: '<p>🎙️</p>',
      contentRichText:
        '<h3>Podcast Host</h3><p>"The Intentional Life" - Top 10 business podcast with 2M+ downloads</p>',
    },
    {
      id: crypto.randomUUID(),
      iconRichText: '<p>🌍</p>',
      contentRichText:
        '<h3>Global Speaker</h3><p>100+ keynotes on 5 continents for Fortune 500 companies and conferences</p>',
    },
  ],
};

export const generateState = createGenerateState(initialState);

export const initialBlockState = {
  type,
  bgcolor: Color.gray50,
  paddingt: 'md' as PaddingSize,
  paddingb: 'md' as PaddingSize,
  cardStyles: DEFAULT_CREDENTIALS_CARD_STYLES,
  iconStyles: DEFAULT_CREDENTIALS_ICON_STYLES,
};

export function MiniView(): JSX.Element {
  return (
    <span class="block py-4">
      <span class="w-4/6 mx-auto block">
        <span class="block bg-gray-500 opacity-75 rounded-sm h-1 mb-3 mx-auto w-1/2"></span>
        <span class="grid grid-cols-2 gap-2">
          <span class="bg-gray-400 opacity-50 rounded p-1 flex items-center gap-1">
            <span class="bg-gray-600 rounded-full h-1 w-1 flex-shrink-0"></span>
            <span class="bg-gray-500 opacity-75 rounded-sm h-1 flex-1"></span>
          </span>
          <span class="bg-gray-400 opacity-50 rounded p-1 flex items-center gap-1">
            <span class="bg-gray-600 rounded-full h-1 w-1 flex-shrink-0"></span>
            <span class="bg-gray-500 opacity-75 rounded-sm h-1 flex-1"></span>
          </span>
          <span class="bg-gray-400 opacity-50 rounded p-1 flex items-center gap-1">
            <span class="bg-gray-600 rounded-full h-1 w-1 flex-shrink-0"></span>
            <span class="bg-gray-500 opacity-75 rounded-sm h-1 flex-1"></span>
          </span>
          <span class="bg-gray-400 opacity-50 rounded p-1 flex items-center gap-1">
            <span class="bg-gray-600 rounded-full h-1 w-1 flex-shrink-0"></span>
            <span class="bg-gray-500 opacity-75 rounded-sm h-1 flex-1"></span>
          </span>
        </span>
      </span>
    </span>
  );
}

function View(
  props: EditorProps<CredentialBlockState> | ViewerProps<CredentialBlockState>,
): JSX.Element {
  const { editProps, readonly } = extractReadonlyProps(props);
  const blockState = extractBlockState(props);
  const cardStyles = blockState?.cardStyles || DEFAULT_CREDENTIALS_CARD_STYLES;
  const iconStyles = blockState?.iconStyles || DEFAULT_CREDENTIALS_ICON_STYLES;

  return (
    <ContainerWrapper class="@lg:py-20 @sm:py-16 py-12 @lg:px-8 @md:px-6 @sm:px-4 px-3">
      <SectionHeader state={props.state} setState={editProps?.setState} />

      <CardLayoutBlock
        blockType={type}
        itemType="Credential"
        state={props.state}
        setState={editProps?.setState || (() => {})}
        emptyCard={{
          iconRichText: '<p>⭐</p>',
          contentRichText: '<h3>New Credential</h3><p>Add your credential details here...</p>',
        }}
        readonly={readonly}
        isBgLight={props.isBgLight}
        isSelected={editProps?.isSelected}
        cardStyles={cardStyles}
        renderCard={({ index, state, setState, readonly, isBgLight }) => (
          <div class="flex flex-col items-start text-left @lg:p-8 @md:p-6 p-5">
            <div
              class={`${getIconClasses(iconStyles)} inline-flex items-center justify-center text-2xl @md:text-3xl transition-all duration-200 mb-4`}
              style={getIconStyle(iconStyles, isBgLight)}
            >
              <RichProp
                state={state}
                setState={readonly ? undefined : setState}
                path={['cards', index, 'iconRichText']}
                class="inline-flex items-center justify-center leading-none w-full h-full"
              />
            </div>

            <div class="w-full">
              <RichProp
                state={state}
                setState={readonly ? undefined : setState}
                path={['cards', index, 'contentRichText']}
                class="@md:text-base text-sm leading-relaxed"
              />
            </div>
          </div>
        )}
      />
    </ContainerWrapper>
  );
}

export const Editor = View;
export const Viewer = View;
